/* eslint-disable react/jsx-no-undef */
/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import PropTypes from "prop-types";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { useMediaQuery, useTheme } from "@mui/material";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

// Custom hooks
import useMemberData from "layouts/tables/data/memberTable";
import Swal from "sweetalert2";
import { API_BASE, getStorageUrl } from "config/api";

// Country codes for international members
const COUNTRY_CODES = {
  Afghanistan: "+93",
  Albania: "+355",
  Algeria: "+213",
  Andorra: "+376",
  Angola: "+244",
  "Antigua and Barbuda": "+1",
  Argentina: "+54",
  Armenia: "+374",
  Australia: "+61",
  Austria: "+43",
  Azerbaijan: "+994",
  Bahamas: "+1",
  Bahrain: "+973",
  Bangladesh: "+880",
  Barbados: "+1",
  Belarus: "+375",
  Belgium: "+32",
  Belize: "+501",
  Benin: "+229",
  Bhutan: "+975",
  Bolivia: "+591",
  "Bosnia and Herzegovina": "+387",
  Botswana: "+267",
  Brazil: "+55",
  Brunei: "+673",
  Bulgaria: "+359",
  "Burkina Faso": "+226",
  Burundi: "+257",
  "Cabo Verde": "+238",
  Cambodia: "+855",
  Cameroon: "+237",
  Canada: "+1",
  "Central African Republic": "+236",
  Chad: "+235",
  Chile: "+56",
  China: "+86",
  Colombia: "+57",
  Comoros: "+269",
  Congo: "+242",
  "Costa Rica": "+506",
  "Côte d'Ivoire": "+225",
  Croatia: "+385",
  Cuba: "+53",
  Cyprus: "+357",
  "Czech Republic": "+420",
  Denmark: "+45",
  Djibouti: "+253",
  Dominica: "+1",
  "Dominican Republic": "+1",
  "DR Congo": "+243",
  Ecuador: "+593",
  Egypt: "+20",
  "El Salvador": "+503",
  "Equatorial Guinea": "+240",
  Eritrea: "+291",
  Estonia: "+372",
  Eswatini: "+268",
  Ethiopia: "+251",
  Fiji: "+679",
  Finland: "+358",
  France: "+33",
  Gabon: "+241",
  Gambia: "+220",
  Georgia: "+995",
  Germany: "+49",
  Ghana: "+233",
  Greece: "+30",
  Grenada: "+1",
  Guatemala: "+502",
  Guinea: "+224",
  "Guinea-Bissau": "+245",
  Guyana: "+592",
  Haiti: "+509",
  Honduras: "+504",
  Hungary: "+36",
  Iceland: "+354",
  India: "+91",
  Indonesia: "+62",
  Iran: "+98",
  Iraq: "+964",
  Ireland: "+353",
  Israel: "+972",
  Italy: "+39",
  Jamaica: "+1",
  Japan: "+81",
  Jordan: "+962",
  Kazakhstan: "+7",
  Kenya: "+254",
  Kiribati: "+686",
  Kuwait: "+965",
  Kyrgyzstan: "+996",
  Laos: "+856",
  Latvia: "+371",
  Lebanon: "+961",
  Lesotho: "+266",
  Liberia: "+231",
  Libya: "+218",
  Liechtenstein: "+423",
  Lithuania: "+370",
  Luxembourg: "+352",
  Madagascar: "+261",
  Malawi: "+265",
  Malaysia: "+60",
  Maldives: "+960",
  Mali: "+223",
  Malta: "+356",
  "Marshall Islands": "+692",
  Mauritania: "+222",
  Mauritius: "+230",
  Mexico: "+52",
  Micronesia: "+691",
  Moldova: "+373",
  Monaco: "+377",
  Mongolia: "+976",
  Montenegro: "+382",
  Morocco: "+212",
  Mozambique: "+258",
  Myanmar: "+95",
  Namibia: "+264",
  Nauru: "+674",
  Nepal: "+977",
  Netherlands: "+31",
  "New Zealand": "+64",
  Nicaragua: "+505",
  Niger: "+227",
  Nigeria: "+234",
  "North Korea": "+850",
  "North Macedonia": "+389",
  Norway: "+47",
  Oman: "+968",
  Pakistan: "+92",
  Palau: "+680",
  Palestine: "+970",
  Panama: "+507",
  "Papua New Guinea": "+675",
  Paraguay: "+595",
  Peru: "+51",
  Philippines: "+63",
  Poland: "+48",
  Portugal: "+351",
  Qatar: "+974",
  Romania: "+40",
  Russia: "+7",
  Rwanda: "+250",
  "Saint Kitts and Nevis": "+1",
  "Saint Lucia": "+1",
  "Saint Vincent and the Grenadines": "+1",
  Samoa: "+685",
  "San Marino": "+378",
  "Sao Tome and Principe": "+239",
  "Saudi Arabia": "+966",
  Senegal: "+221",
  Serbia: "+381",
  Seychelles: "+248",
  "Sierra Leone": "+232",
  Singapore: "+65",
  Slovakia: "+421",
  Slovenia: "+386",
  "Solomon Islands": "+677",
  Somalia: "+252",
  "South Africa": "+27",
  "South Korea": "+82",
  "South Sudan": "+211",
  Spain: "+34",
  "Sri Lanka": "+94",
  Sudan: "+249",
  Suriname: "+597",
  Sweden: "+46",
  Switzerland: "+41",
  Syria: "+963",
  Taiwan: "+886",
  Tajikistan: "+992",
  Tanzania: "+255",
  Thailand: "+66",
  "Timor-Leste": "+670",
  Togo: "+228",
  Tonga: "+676",
  "Trinidad and Tobago": "+1",
  Tunisia: "+216",
  Turkey: "+90",
  Turkmenistan: "+993",
  Tuvalu: "+688",
  Uganda: "+256",
  Ukraine: "+380",
  "United Arab Emirates": "+971",
  "United Kingdom": "+44",
  "United States": "+1",
  Uruguay: "+598",
  Uzbekistan: "+998",
  Vanuatu: "+678",
  "Vatican City": "+379",
  Venezuela: "+58",
  Vietnam: "+84",
  Yemen: "+967",
  Zambia: "+260",
  Zimbabwe: "+263",
};

// Helper function to get country code
const getCountryCode = (country) => {
  return COUNTRY_CODES[country] || "+233";
};

function Member() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Bulk credential-send dialog
  const [credDialogOpen, setCredDialogOpen] = useState(false);
  const [credScope, setCredScope] = useState("all"); // all | ghana | international
  const [credSending, setCredSending] = useState(false);

  const handleBulkSendCredentials = async () => {
    setCredSending(true);
    try {
      const token = localStorage.getItem("authToken");
      const churchRaw = localStorage.getItem("church");
      const church = churchRaw ? JSON.parse(churchRaw) : null;
      if (!church?.id) throw new Error("No church loaded.");

      const body = { church_id: church.id };
      if (credScope === "ghana") body.country = "Ghana";

      const res = await fetch(`${API_BASE}/members/send-credentials-bulk`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Bulk send failed");

      // If scope was "international", filter client-side from the result
      const data = json?.data || {};
      Swal.fire({
        icon: "success",
        title: "Login credentials sent",
        html:
          `<div style="text-align:left">` +
          `<p><strong>${data.delivered}</strong> delivered ` +
          `(out of ${data.total} attempted)</p>` +
          (data.failed
            ? `<p style="color:#b71c1c"><strong>${data.failed}</strong> failed — these members had no usable phone or email.</p>`
            : "") +
          `</div>`,
      });
      setCredDialogOpen(false);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Send failed", text: e.message });
    } finally {
      setCredSending(false);
    }
  };
  const {
    columns,
    rows,
    filteredRows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    showModalOpen,
    setShowModalOpen,
    selectedMember,
    setSelectedMember,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMember,
    departments,
    ministries,
    branches,
    MemberDetailsModal,
    handleSearch,
    searchQuery,
    filterMinistry,
    filterGender,
    filterAge,
    handleFilterMinistry,
    handleFilterGender,
    handleFilterAge,
    isSaving,
    photoPreview,
    signaturePreview,
    handlePhotoUpload,
    handleSignatureUpload,
  } = useMemberData();

  // Hide secondary columns on small screens so the table (and its action
  // buttons) doesn't overflow — keep just the essentials + Actions.
  const visibleColumns = React.useMemo(() => {
    if (!isMobile) return columns;
    const keep = ["photo", "firstName", "surname", "status", "actions"];
    return columns.filter((col) => keep.includes(col.accessor));
  }, [columns, isMobile]);

  const [localSearchQuery, setLocalSearchQuery] = useState("");

  // Debug useEffect to check selectedMember data
  useEffect(() => {
    if (selectedMember) {
      console.log("Selected Member Data:", {
        id: selectedMember.id,
        firstName: selectedMember.firstName,
        areYouBaptized: selectedMember.areYouBaptized,
        photo: selectedMember.photo,
        photoPreview: photoPreview,
      });
    }
  }, [selectedMember, photoPreview]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    handleSearch(value);
  };

  const validateStep1 = () => {
    if (!selectedMember?.firstName) return "First name is required";
    if (!selectedMember?.surname) return "Surname is required";
    if (!selectedMember?.gender) return "Gender is required";
    if (!selectedMember?.residentialAddress) return "Residential address is required";
    if (!selectedMember?.country) return "Country is required";
    if (!selectedMember?.maritalStatus) return "Marital Status is required";
    return null;
  };

  const validateStep2 = () => {
    if (!selectedMember?.areYouBaptized) return "Are you Baptized Field is required";
    return null;
  };

  const validateStep3 = () => {
    if (!selectedMember?.departmentId) return "Department Field is required";
    if (!selectedMember?.willYouPayTithe) return "Will you Pay Tithe is required";
    if (!selectedMember?.willYouPayWelfare) return "Will you Pay Welfare is required";
    if (!selectedMember?.membershipDate) return "Membership Date is required";
    return null;
  };

  const validateMemberForm = () => validateStep1() || validateStep2() || validateStep3();

  const handleSaveMember = () => {
    const errors = validateMemberForm();
    if (errors) {
      showErrorToast(errors);
      return;
    }
    handleEditSubmit();
  };

  const showErrorToast = (message) => {
    Swal.fire({
      title: "Error!",
      text: message,
      icon: "error",
      position: "top",
      toast: true,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
  };

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading members...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Church Members"
        subtitle="Manage member registrations, profiles, and login access."
        actions={
          <>
            <Button variant="contained" startIcon={<Icon>add</Icon>} onClick={handleAddMember}>
              New Member
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Icon>key</Icon>}
              onClick={() => setCredDialogOpen(true)}
            >
              Send Logins
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <Icon fontSize="small">print</Icon>
            </IconButton>
          </>
        }
      />

      <Card>
        {/* Search + Filters */}
        <Box p={3} pb={0}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                value={localSearchQuery}
                onChange={handleSearchChange}
                placeholder="Search by full name, member ID, country, or phone..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Gender</InputLabel>
                <Select
                  label="Gender"
                  value={filterGender}
                  onChange={(e) => handleFilterGender(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ministry</InputLabel>
                <Select
                  label="Ministry"
                  value={filterMinistry}
                  onChange={(e) => handleFilterMinistry(e.target.value)}
                >
                  <MenuItem value="">All Ministries</MenuItem>
                  {ministries.map((m) => (
                    <MenuItem key={m.id} value={m.name}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Age Group</InputLabel>
                <Select
                  label="Age Group"
                  value={filterAge}
                  onChange={(e) => handleFilterAge(e.target.value)}
                >
                  <MenuItem value="">All Ages</MenuItem>
                  <MenuItem value="0-17">0 – 17</MenuItem>
                  <MenuItem value="18-35">18 – 35</MenuItem>
                  <MenuItem value="36-50">36 – 50</MenuItem>
                  <MenuItem value="51+">51+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Typography variant="caption" color="text.secondary">
                {filteredRows?.length ?? 0} member{filteredRows?.length !== 1 ? "s" : ""}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box pt={3}>
          <DataTable
            table={{ columns: visibleColumns, rows: filteredRows || rows }}
            isSorted={false}
            entriesPerPage
            showTotalEntries
            noEndBorder
            canSearch={false}
          />
        </Box>
      </Card>

      {/* Edit Member Modal */}
      <FormDialog
        open={editModalOpen}
        onClose={handleCloseModal}
        title={selectedMember?.id ? "Edit Member" : "New Membership Form"}
        onSave={handleSaveMember}
        saveLabel={selectedMember?.id ? "Save Changes" : "Create Member"}
        loading={isSaving}
        maxWidth="lg"
      >
        {/* Member ID field for editing */}
        {selectedMember?.id && (
          <FormSection>
            <TextField
              label="Member ID"
              value={selectedMember?.memberId || ""}
              onChange={(e) => setSelectedMember({ ...selectedMember, memberId: e.target.value })}
              fullWidth
              margin="normal"
              helperText="Unique identifier for the member"
            />
          </FormSection>
        )}

        <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
          Basic Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="First Name"
                    value={selectedMember?.firstName || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, firstName: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                    required
                    error={!selectedMember?.firstName}
                    helperText={!selectedMember?.firstName ? "This field is required" : ""}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Surname"
                    value={selectedMember?.surname || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, surname: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                    required
                    error={!selectedMember?.surname}
                    helperText={!selectedMember?.surname ? "This field is required" : ""}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <TextField
                label="Middle Name"
                value={selectedMember?.middleName || ""}
                onChange={(e) =>
                  setSelectedMember({ ...selectedMember, middleName: e.target.value })
                }
                fullWidth
                margin="normal"
              />
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="gender-select-label" required>
                      Gender
                    </InputLabel>
                    <Select
                      labelId="gender-select-label"
                      value={selectedMember?.gender || ""}
                      label="Gender"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, gender: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={selectedMember?.dateOfBirth || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, dateOfBirth: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="marital-status-select-label" required>
                      Marital Status
                    </InputLabel>
                    <Select
                      labelId="marital-status-select-label"
                      value={selectedMember?.maritalStatus || ""}
                      label="Marital Status"
                      error={!selectedMember?.maritalStatus}
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, maritalStatus: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Single">Single</MenuItem>
                      <MenuItem value="Married">Married</MenuItem>
                      <MenuItem value="Divorced">Divorced</MenuItem>
                      <MenuItem value="Widowed">Widowed</MenuItem>
                      <MenuItem value="Separated">Separated</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Email"
                    type="email"
                    value={selectedMember?.email || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, email: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Country"
                    value={selectedMember?.country || ""}
                    fullWidth
                    margin="normal"
                    required
                    error={!selectedMember?.country}
                    select
                    onChange={(e) => {
                      const selectedCountry = e.target.value;
                      const countryCode = getCountryCode(selectedCountry);
                      setSelectedMember({
                        ...selectedMember,
                        country: selectedCountry,
                        country_code: countryCode,
                        phone_country_code: countryCode,
                      });
                    }}
                  >
                    {Object.keys(COUNTRY_CODES)
                      .sort()
                      .map((country) => (
                        <MenuItem key={country} value={country}>
                          {country} ({COUNTRY_CODES[country]})
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Region/State/Province"
                    value={selectedMember?.region || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, region: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                    helperText="Region, State, or Province"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Home Town/City"
                    value={selectedMember?.homeTown || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, homeTown: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Residential Address"
                    value={selectedMember?.residentialAddress || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, residentialAddress: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                    required
                    error={!selectedMember?.residentialAddress}
                    helperText={!selectedMember?.residentialAddress ? "This field is required" : ""}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSection>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl sx={{ minWidth: 100 }}>
                  <InputLabel>Code</InputLabel>
                  <Select
                    value={
                      selectedMember?.phone_country_code || selectedMember?.country_code || "+233"
                    }
                    label="Code"
                    onChange={(e) => {
                      setSelectedMember({
                        ...selectedMember,
                        phone_country_code: e.target.value,
                      });
                    }}
                    sx={{ height: 50 }}
                  >
                    {Object.entries(COUNTRY_CODES)
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map(([country, code]) => (
                        <MenuItem key={country} value={code}>
                          {country} ({code})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Phone Number"
                  value={selectedMember?.phoneNumber || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, phoneNumber: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  sx={{ flex: 1 }}
                />
              </Box>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Other Number"
                    value={selectedMember?.otherNumber || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, otherNumber: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Whatsapp Number"
                    value={selectedMember?.whatsappNumber || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, whatsappNumber: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <TextField
                label="Postal Address"
                value={selectedMember?.postalAddress || ""}
                onChange={(e) =>
                  setSelectedMember({ ...selectedMember, postalAddress: e.target.value })
                }
                fullWidth
                margin="normal"
              />
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Profession"
                    value={selectedMember?.profession || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, profession: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Rank / Position"
                    value={selectedMember?.rank || ""}
                    onChange={(e) => setSelectedMember({ ...selectedMember, rank: e.target.value })}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="employment-select-label">Employment</InputLabel>
                    <Select
                      labelId="employment-select-label"
                      value={selectedMember?.employment || ""}
                      label="Employment"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, employment: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Civil Servant">Civil Servant</MenuItem>
                      <MenuItem value="Employed">Employed</MenuItem>
                      <MenuItem value="Self Employed">Self Employed</MenuItem>
                      <MenuItem value="Apprentice">Apprentice</MenuItem>
                      <MenuItem value="Student">Student</MenuItem>
                      <MenuItem value="Retired">Retired</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Name of Employer"
                    value={selectedMember?.nameOfEmployer || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, nameOfEmployer: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <FormControl fullWidth margin="normal">
                <InputLabel id="education-select-label">Education</InputLabel>
                <Select
                  labelId="education-select-label"
                  value={selectedMember?.education || ""}
                  label="Education"
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, education: e.target.value })
                  }
                  sx={{ minHeight: 50 }}
                >
                  <MenuItem value="None">None</MenuItem>
                  <MenuItem value="Basic">Basic</MenuItem>
                  <MenuItem value="Secondary">Secondary</MenuItem>
                  <MenuItem value="Tertiary">Tertiary</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </FormSection>
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
          Detailed Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Name of Spouse"
                    value={selectedMember?.spouseName || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, spouseName: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Spouse's Phone Number"
                    value={selectedMember?.spousePhoneNumber || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, spousePhoneNumber: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Date of Marriage"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={selectedMember?.dateOfMarriage || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, dateOfMarriage: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="By Pastor / Reverend"
                    value={selectedMember?.byPastor || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, byPastor: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Number of Children"
                    type="number"
                    value={selectedMember?.numberOfChildren || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, numberOfChildren: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Male Children"
                    type="number"
                    value={selectedMember?.numberOfMales || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, numberOfMales: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Female Children"
                    type="number"
                    value={selectedMember?.numberOfFemales || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, numberOfFemales: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Names of Father"
                    value={selectedMember?.nameOfFather || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, nameOfFather: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Father Life Status</InputLabel>
                    <Select
                      label="Father Life Status"
                      value={selectedMember?.fatherLifeStatus || ""}
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, fatherLifeStatus: e.target.value })
                      }
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      <MenuItem value="alive">Alive</MenuItem>
                      <MenuItem value="deceased">Deceased</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Names of Mother"
                    value={selectedMember?.nameOfMother || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, nameOfMother: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Mother Life Status</InputLabel>
                    <Select
                      label="Mother Life Status"
                      value={selectedMember?.motherLifeStatus || ""}
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, motherLifeStatus: e.target.value })
                      }
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      <MenuItem value="alive">Alive</MenuItem>
                      <MenuItem value="deceased">Deceased</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Next of Kin"
                    value={selectedMember?.nextOfKin || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, nextOfKin: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Next of Kin Relationship"
                    value={selectedMember?.nextOfKinRelationship || ""}
                    onChange={(e) =>
                      setSelectedMember({
                        ...selectedMember,
                        nextOfKinRelationship: e.target.value,
                      })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Next of Kin Phone Number"
                    value={selectedMember?.nextOfKinPhone || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, nextOfKinPhone: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Year of Conversion"
                    type="number"
                    value={selectedMember?.yearOfConversion || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, yearOfConversion: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="baptized-select-label" required>
                      Are you Baptized?
                    </InputLabel>
                    <Select
                      labelId="baptized-select-label"
                      value={selectedMember?.areYouBaptized || ""}
                      label="Are you Baptized?"
                      onChange={(e) => {
                        setSelectedMember({ ...selectedMember, areYouBaptized: e.target.value });
                      }}
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="baptism-type-select-label">Baptism Type</InputLabel>
                    <Select
                      labelId="baptism-type-select-label"
                      value={selectedMember?.baptismType || ""}
                      label="Baptism Type"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, baptismType: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Immersion">Immersion</MenuItem>
                      <MenuItem value="Sprinkling">Sprinkling</MenuItem>
                      <MenuItem value="Pouring">Pouring</MenuItem>
                      <MenuItem value="Not Applicable">Not Applicable</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Date of Baptism"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={selectedMember?.baptismDate || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, baptismDate: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Baptism Church"
                    value={selectedMember?.baptismChurch || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, baptismChurch: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <TextField
                label="Baptized By"
                value={selectedMember?.baptismBy || ""}
                onChange={(e) =>
                  setSelectedMember({ ...selectedMember, baptismBy: e.target.value })
                }
                fullWidth
                margin="normal"
              />
            </FormSection>
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
          Final Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Name of Previous Church"
                    value={selectedMember?.nameOfPreviousChurch || ""}
                    onChange={(e) =>
                      setSelectedMember({
                        ...selectedMember,
                        nameOfPreviousChurch: e.target.value,
                      })
                    }
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="interest-select-label">Place of Interest</InputLabel>
                    <Select
                      labelId="interest-select-label"
                      value={selectedMember?.placeOfInterest || ""}
                      label="Place of Interest"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, placeOfInterest: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Music Ministry">Music Ministry</MenuItem>
                      <MenuItem value="Ushering">Ushering</MenuItem>
                      <MenuItem value="Prayer Tower">Prayer Tower</MenuItem>
                      <MenuItem value="Evangelism">Evangelism</MenuItem>
                      <MenuItem value="Teaching">Teaching</MenuItem>
                      <MenuItem value="Protocol">Protocol</MenuItem>
                      <MenuItem value="Outside Cleaners">Outside Cleaners</MenuItem>
                      <MenuItem value="F.O.C">F.O.C</MenuItem>
                      <MenuItem value="Janitors">Janitors</MenuItem>
                      <MenuItem value="Media">Media</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="department-select-label" required>
                      Department
                    </InputLabel>
                    <Select
                      labelId="department-select-label"
                      value={selectedMember?.departmentId || ""}
                      label="Department"
                      error={!selectedMember?.departmentId}
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, departmentId: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="ministry-select-label">Ministry</InputLabel>
                    <Select
                      labelId="ministry-select-label"
                      value={selectedMember?.ministryId || ""}
                      label="Ministry"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, ministryId: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      {ministries.map((min) => (
                        <MenuItem key={min.id} value={min.id}>
                          {min.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="branch-select-label">Branch</InputLabel>
                    <Select
                      labelId="branch-select-label"
                      value={selectedMember?.branchId || ""}
                      label="Branch"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, branchId: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="tithe-select-label" required>
                      Will you pay Tithe?
                    </InputLabel>
                    <Select
                      labelId="tithe-select-label"
                      value={selectedMember?.willYouPayTithe || ""}
                      label="Will you pay Tithe?"
                      error={!selectedMember?.willYouPayTithe}
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, willYouPayTithe: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="communicant-select-label">Communicant</InputLabel>
                    <Select
                      labelId="communicant-select-label"
                      value={selectedMember?.communicant || ""}
                      label="Communicant"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, communicant: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="welfare-select-label" required>
                      Will You Pay Welfare?
                    </InputLabel>
                    <Select
                      labelId="welfare-select-label"
                      value={selectedMember?.willYouPayWelfare || ""}
                      label="Will You Pay Welfare?"
                      error={!selectedMember?.willYouPayWelfare}
                      onChange={(e) =>
                        setSelectedMember({
                          ...selectedMember,
                          willYouPayWelfare: e.target.value,
                        })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Membership Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={selectedMember?.membershipDate || ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, membershipDate: e.target.value })
                    }
                    fullWidth
                    margin="normal"
                    required
                    error={!selectedMember?.membershipDate}
                    helperText={!selectedMember?.membershipDate ? "This field is required" : ""}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSection>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Role"
                    value={selectedMember?.role || ""}
                    onChange={(e) => setSelectedMember({ ...selectedMember, role: e.target.value })}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      value={selectedMember?.status || "active"}
                      label="Status"
                      onChange={(e) =>
                        setSelectedMember({ ...selectedMember, status: e.target.value })
                      }
                      sx={{ minHeight: 50 }}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              <TextField
                label="Languages Spoken"
                value={selectedMember?.languagesSpoken || ""}
                onChange={(e) =>
                  setSelectedMember({ ...selectedMember, languagesSpoken: e.target.value })
                }
                fullWidth
                margin="normal"
                placeholder="e.g., English, French, Twi"
              />
            </FormSection>
          </Grid>
        </Grid>

        <FormSection>
          <Grid container spacing={2}>
            {/* PHOTO */}
            <Grid item xs={12} sm={6}>
              <Typography variant="button" fontWeight={700} mb={1} display="block">
                <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 18 }}>image</Icon>
                Member Photo
              </Typography>
              <Box
                component="label"
                htmlFor="member-photo-input"
                sx={{
                  mt: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  border: "2px dashed rgba(26,115,232,0.4)",
                  backgroundColor: "rgba(26,115,232,0.04)",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "all .2s ease",
                  minHeight: 220,
                  "&:hover": {
                    backgroundColor: "rgba(26,115,232,0.08)",
                    borderColor: "rgba(26,115,232,0.7)",
                  },
                }}
              >
                {photoPreview || selectedMember?.photo ? (
                  <Box sx={{ position: "relative", width: "100%", height: 220 }}>
                    <img
                      src={photoPreview || getStorageUrl(selectedMember?.photo)}
                      alt="Member preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                      onError={(e) => {
                        e.currentTarget.src =
                          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><rect fill='%23eee' width='200' height='200'/><text x='50%25' y='50%25' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle' dy='.3em'>No image</text></svg>";
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        color: "white",
                        p: 1.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} sx={{ color: "white" }}>
                        <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 16 }}>upload</Icon>
                        Click to replace
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box textAlign="center" p={3}>
                    <Icon sx={{ fontSize: 48, color: "rgba(26,115,232,0.7)" }}>cloud_upload</Icon>
                    <Typography variant="body2" fontWeight={700} color="info.main" mt={1}>
                      Click to upload a photo
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PNG, JPG up to 5 MB
                    </Typography>
                  </Box>
                )}
                <input
                  id="member-photo-input"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: "none" }}
                />
              </Box>
            </Grid>

            {/* SIGNATURE */}
            <Grid item xs={12} sm={6}>
              <Typography variant="button" fontWeight={700} mb={1} display="block">
                <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 18 }}>draw</Icon>
                Signature
              </Typography>
              <Box
                component="label"
                htmlFor="member-signature-input"
                sx={{
                  mt: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  border: "2px dashed rgba(155,89,182,0.4)",
                  backgroundColor: "rgba(155,89,182,0.04)",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "all .2s ease",
                  minHeight: 220,
                  "&:hover": {
                    backgroundColor: "rgba(155,89,182,0.08)",
                    borderColor: "rgba(155,89,182,0.7)",
                  },
                }}
              >
                {signaturePreview || selectedMember?.signature ? (
                  <Box sx={{ position: "relative", width: "100%", height: 220, p: 2 }}>
                    <img
                      src={signaturePreview || getStorageUrl(selectedMember?.signature)}
                      alt="Signature preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        display: "block",
                        background: "white",
                        borderRadius: 4,
                      }}
                      onError={(e) => {
                        e.currentTarget.src =
                          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='100'><rect fill='%23eee' width='300' height='100'/><text x='50%25' y='50%25' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle' dy='.3em'>No signature</text></svg>";
                      }}
                    />
                  </Box>
                ) : (
                  <Box textAlign="center" p={3}>
                    <Icon sx={{ fontSize: 48, color: "rgba(155,89,182,0.7)" }}>gesture</Icon>
                    <Typography variant="body2" fontWeight={700} color="secondary" mt={1}>
                      Click to upload a signature
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PNG, JPG — preferably transparent
                    </Typography>
                  </Box>
                )}
                <input
                  id="member-signature-input"
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  style={{ display: "none" }}
                />
              </Box>
            </Grid>
          </Grid>
        </FormSection>
      </FormDialog>

      {/* Delete Member Modal */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${
          selectedMember?.memberName || `${selectedMember?.firstName} ${selectedMember?.surname}`
        }" member?`}
        confirmLabel="Delete"
      />

      {/* Show Member Details Modal */}
      {MemberDetailsModal && (
        <MemberDetailsModal
          member={selectedMember}
          open={showModalOpen}
          onClose={() => setShowModalOpen(false)}
        />
      )}

      {/* Bulk credential-send dialog */}
      <FormDialog
        open={credDialogOpen}
        onClose={() => !credSending && setCredDialogOpen(false)}
        title="Send login credentials"
        onSave={handleBulkSendCredentials}
        saveLabel="Send"
        loading={credSending}
        maxWidth="xs"
      >
        <FormSection>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Each member will receive their username (phone or email) and the default password{" "}
            <strong>12345678</strong>. Ghana members get an SMS, others get an email.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Send to</InputLabel>
            <Select
              value={credScope}
              label="Send to"
              onChange={(e) => setCredScope(e.target.value)}
            >
              <MenuItem value="all">All members in this church</MenuItem>
              <MenuItem value="ghana">Ghana members only (SMS)</MenuItem>
            </Select>
          </FormControl>
        </FormSection>
      </FormDialog>
    </Box>
  );
}

Member.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedMember: PropTypes.object,
  setSelectedMember: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddMember: PropTypes.func,
};

export default Member;
